import { apiV2 } from '../lib/baseAxios';
import config from '../../config';
import { getToken } from '../lib/localStorageHandler';
import { getCurrentOutputLanguageName } from '../hooks/useOutputLanguage';

export type TemplateScope = 'personal' | 'team' | 'public';

export interface Template {
  _id: string;
  id: string;
  teamId: string;
  ownerId: string;
  scope: TemplateScope;
  title: string;
  description?: string;
  prompt: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateTemplateParams {
  id?: string; // Make ID optional for creation
  title: string;
  description?: string;
  prompt: string;
  scope?: TemplateScope; // Optional, defaults to 'personal' on backend
}

const templatesUrl = `/templates`;

export const createTemplate = async (params: CreateTemplateParams) => {
  const response = await apiV2.post(templatesUrl, params);
  return response.data;
};

export const getTemplates = async (
  scope?: TemplateScope
): Promise<Template[] | null> => {
  try {
    const params = scope ? { scope } : {};
    const response = await apiV2.get(templatesUrl, { params });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch templates:', error);
    return null;
  }
};

export const updateTemplate = async (
  id: string,
  template: Partial<
    Omit<
      Template,
      '_id' | 'createdAt' | 'updatedAt' | 'id' | 'ownerId' | 'teamId'
    >
  >,
  scope: TemplateScope // Now REQUIRED to avoid ambiguity
) => {
  const params = { scope };
  const response = await apiV2.put(`${templatesUrl}/${id}`, template, {
    params,
  });
  return response.data;
};

export const deleteTemplate = async (id: string, scope: TemplateScope) => {
  const params = { scope };
  const response = await apiV2.delete(`${templatesUrl}/${id}`, { params });
  return response.data;
};

export const generateTitle = async (
  customTemplate: string
): Promise<string> => {
  // Get current output language setting
  const outputLanguageName = await getCurrentOutputLanguageName();

  const response = await fetch(`${config.authBaseUrlV2}/proxy/ai/completion`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({
      prompt: `Generate a concise title (max 15 characters, use complete words, no abbreviations) in ${outputLanguageName} for this template prompt: "${customTemplate.trim()}". Return only plain text without any formatting, asterisks, or markdown.`,
      model: 'fast',
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to generate title');
  }

  const data = await response.text();
  // Remove any asterisks that might still appear and trim whitespace
  return data.replace(/\*+/g, '').trim();
};

export const getTemplate = async (
  id: string,
  scope?: TemplateScope
): Promise<Template | null> => {
  try {
    const params = scope ? { scope } : {};
    const response = await apiV2.get(`${templatesUrl}/${id}`, { params });
    return response.data.data;
  } catch (error) {
    if ((error as any)?.response?.status === 404) {
      return null;
    }
    console.error('Failed to fetch template:', error);
    throw error;
  }
};
